/**
 * 1QA Device Agent (docs/design/device-agent.md): a small app on a tester's computer that lets 1QA run tests on the
 * phones connected to it. The agent only makes outbound HTTPS calls to the API: it reports its devices (hello) and
 * long-polls for work. Runners hand it work through Redis (the API relays), so a tester's computer never needs to
 * be reachable and never sees the database, Redis or storage.
 */
import { z } from 'zod';
import type { ServerCertForAgent } from './serverCerts.js';
import { RUN_QUEUE_NAME, type Platform } from './testing.js';

/** Agent tokens: shown once when the agent is created, stored as a SHA-256 hash. */
export const AGENT_TOKEN_PREFIX = '1qa_da_';

/** Device profile ids of agent devices: `agent:<agentId>:<serial>` (see DEVICE_PROFILE_RE). */
export const AGENT_PROFILE_PREFIX = 'agent:';

/** Runs on agent devices go to this queue; runners with "agent" in RUNNER_PLATFORMS serve it (no Android SDK needed). */
export const AGENT_QUEUE_NAME = `${RUN_QUEUE_NAME}-agent`;

/** adb serials and iOS UDIDs the agent may report (no spaces, no path characters beyond these). */
export const AGENT_SERIAL_RE = /^[A-Za-z0-9._:-]{1,80}$/;

export const isAgentProfile = (profile: string | null | undefined): profile is string => !!profile?.startsWith(AGENT_PROFILE_PREFIX);

export function agentProfileId(agentId: string, serial: string): string {
  return `${AGENT_PROFILE_PREFIX}${agentId}:${serial}`;
}

/** { agentId, serial } of an agent device profile id, or null. */
export function parseAgentProfile(profile: string): { agentId: string; serial: string } | null {
  if (!isAgentProfile(profile)) return null;
  const rest = profile.slice(AGENT_PROFILE_PREFIX.length);
  const i = rest.indexOf(':');
  if (i <= 0) return null;
  const agentId = rest.slice(0, i);
  const serial = rest.slice(i + 1);
  return /^[a-z0-9]{10,40}$/.test(agentId) && AGENT_SERIAL_RE.test(serial) ? { agentId, serial } : null;
}

/** A device as the agent reports it. */
export const agentDeviceSchema = z.object({
  serial: z.string().regex(AGENT_SERIAL_RE),
  platform: z.enum(['ANDROID', 'IOS']),
  /** adb state: "device" is usable; "unauthorized" means the phone has not accepted this computer yet. */
  state: z.string().max(40),
  model: z.string().max(100).nullable(),
  os: z.string().max(20).nullable(),
  sdk: z.number().int().nullable(),
});
export type AgentDevice = z.infer<typeof agentDeviceSchema>;

/** POST /agent/v1/hello: the agent's heartbeat, every few seconds. */
export const agentHelloSchema = z.object({
  hostname: z.string().max(100),
  agentVersion: z.string().max(40),
  devices: z.array(agentDeviceSchema).max(50),
});
export type AgentHello = z.infer<typeof agentHelloSchema>;

/** Agent heartbeats expire after this: no hello for that long and the agent counts as offline. */
export const AGENT_HEARTBEAT_TTL_S = 30;
export const AGENT_HELLO_EVERY_MS = 10_000;
/** Longest wait of one GET /agent/v1/work before it answers 204 (the agent asks again at once). */
export const AGENT_POLL_WAIT_S = 25;

/**
 * Work for an agent. `http`: forward a request of the test engine to the agent's local agent-device proxy (only its
 * /agent-device/ routes). The rest act on one of the agent's devices with adb.
 */
export type AgentJob = { id: string; /** Epoch ms after which the job is stale. */ expiresAt: number } & (
  | { kind: 'http'; method: string; path: string; headers: Record<string, string>; body: string | null }
  | { kind: 'install'; serial: string; appId: string; build: { id: string; sha256: string; fileName: string } }
  | { kind: 'check-app'; serial: string; appId: string }
  | { kind: 'clear-log'; serial: string }
  | { kind: 'app-log'; serial: string; appId: string }
  | { kind: 'screenshot'; serial: string }
  /**
   * Record what the tester does on the phone (agent 1.1.0+, docs/design/mobile-recorder.md). The agent answers at
   * once and then posts the session's events to /agent/recordings/:recordingId/events until told to stop.
   */
  | {
      kind: 'record-start';
      recordingId: string;
      serial: string;
      appId: string;
      build: { id: string; sha256: string; fileName: string } | null;
      /** Also record the app's network traffic through the agent's recording proxy (agent 1.4.0+). */
      traffic?: boolean;
      /** The performance project's own server certificates (with keys): those hosts are opened with them. */
      serverCerts?: ServerCertForAgent[];
    }
);

/** An agent's answer. `http`: status, headers and base64 body of the proxy's response. Others: ok / error / data. */
export type AgentJobResult =
  | { kind: 'http'; status: number; headers: Record<string, string>; body: string }
  | { kind: 'done'; ok: boolean; error?: string; data?: string };

/** Paths an http job may reach on the agent's proxy: the test engine's routes, nothing else. */
export const AGENT_PROXY_PATH_RE = /^(?!.*\/\.\.?(\/|\?|$))\/agent-device\/(rpc|upload|artifacts|health)(\/[A-Za-z0-9._~\/-]*)?(\?[A-Za-z0-9._~=&%-]*)?$/;

export interface AgentDeviceView extends AgentDevice {
  /** Device profile id for the Device picker and runs. */
  profile: string;
  /** "Pixel 7 · Android 15". */
  label: string;
  /** ready: usable now; busy: running a test; offline/unauthorized: see the agent. */
  status: 'ready' | 'busy' | 'unauthorized' | 'offline';
}

export interface DeviceAgentView {
  id: string;
  name: string;
  tokenPrefix: string;
  createdBy: { id: string; name: string };
  createdAt: string;
  /** Last hello (heartbeat) from the agent. */
  lastSeenAt: string | null;
  online: boolean;
  /** Paused agents get no tests (their devices show as busy to the picker). */
  paused: boolean;
  hostname: string | null;
  agentVersion: string | null;
  devices: AgentDeviceView[];
  /** The signed-in person may pause, rename or remove it (its creator or a Project Admin). */
  canManage: boolean;
}

export const deviceAgentInputSchema = z.object({ name: z.string().trim().min(1).max(60) });
export const deviceAgentUpdateSchema = z.object({ name: z.string().trim().min(1).max(60).optional(), paused: z.boolean().optional() });

/** "Pixel 7 · Android 15" for an agent device. */
export function agentDeviceLabel(d: Pick<AgentDevice, 'model' | 'os' | 'platform'>): string {
  const name = d.model?.trim() || (d.platform === 'IOS' ? 'iPhone' : 'Android phone');
  return d.os ? `${name} · ${d.platform === 'IOS' ? 'iOS' : 'Android'} ${d.os}` : name;
}

/** Project platforms an agent device can run tests for. */
export const agentDeviceFits = (device: Pick<AgentDevice, 'platform'>, project: { platform: Platform }) => device.platform === project.platform;

/** Redis keys shared by the API (agent side) and runners (test side). */
export const agentKeys = {
  /** The agent's last hello (JSON AgentHello + at), expires after AGENT_HEARTBEAT_TTL_S. */
  heartbeat: (agentId: string) => `1qa:agent:hb:${agentId}`,
  /** Jobs for the agent (runners RPUSH, the agent's long-poll LPOPs). */
  inbox: (agentId: string) => `1qa:agent:inbox:${agentId}`,
  /** The answer to one job (the API RPUSHes it, the runner waits on it). */
  reply: (jobId: string) => `1qa:agent:reply:${jobId}`,
  /** Held by the runner while a test uses the device (one test per device). */
  lock: (agentId: string, serial: string) => `1qa:agent:lock:${agentId}:${serial}`,
};

/** A job not picked up within this long is dropped (its runner has given up). */
export const AGENT_JOB_TTL_MS = 60_000;
